import { useMutation, useRelayEnvironment } from 'react-relay';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchQuery } from 'relay-runtime';
import { recordExperimentSpeculativeData } from '../graphQLOperations/Experiments';
import { getExperimentSpeculativeData } from '../graphQLOperations/Experiments';
import type { ExperimentsRecordExperimentsSpeculativeDataMutation } from '../graphQLOperations/__generated__/ExperimentsRecordExperimentsSpeculativeDataMutation.graphql';
import type { ExperimentsGetSpeculativeDataQuery } from '../graphQLOperations/__generated__/ExperimentsGetSpeculativeDataQuery.graphql';

type Updater<T> = T | ((prev: T) => T);

export interface SaveSpeculativeDataParams {
  userNodeId: string;
  flagName: string;
  variantName: string;
  payload: unknown;
}

export interface UseExperimentSpeculativeDataParams<TData> {
  publicUserId?: string;
  userNodeId?: string;
  flagName: string;
  variantName: string;
  defaultValues?: TData;
  debounceMs?: number;
  enabled?: boolean;
  validate?: (value: unknown) => value is TData;
  merge?: (defaults: TData, persisted: TData) => TData;
}

export interface UseExperimentSpeculativeDataReturn<TData> {
  data: TData;
  setData: (updater: Updater<TData>) => void;
  reload: () => void;
  hydrated: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: Error | null;
  saveSpeculativeData: (params: SaveSpeculativeDataParams) => Promise<boolean>;
}

interface PersistedPayloadEnvelope<TData> {
  version: number;
  savedAt: string;
  data: TData;
}

const DEFAULT_DEBOUNCE_MS = 500;

function isPersistedEnvelope<TData>(value: unknown): value is PersistedPayloadEnvelope<TData> {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<PersistedPayloadEnvelope<TData>>;
  return (
    typeof candidate.version === 'number' &&
    typeof candidate.savedAt === 'string' &&
    Object.prototype.hasOwnProperty.call(candidate, 'data')
  );
}

function parsePayloadRootElement(rawRootElement: unknown): unknown {
  if (rawRootElement === null || rawRootElement === undefined) {
    return null;
  }

  if (typeof rawRootElement === 'string') {
    try {
      return JSON.parse(rawRootElement);
    } catch (_error) {
      return null;
    }
  }

  return rawRootElement;
}

export function useExperimentSpeculativeData<TData>({
  publicUserId,
  userNodeId,
  flagName,
  variantName,
  defaultValues,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  enabled = true,
  validate,
  merge,
}: UseExperimentSpeculativeDataParams<TData>): UseExperimentSpeculativeDataReturn<TData> {
  const environment = useRelayEnvironment();
  const [commit, isInFlight] = useMutation<ExperimentsRecordExperimentsSpeculativeDataMutation>(
    recordExperimentSpeculativeData,
  );

  const [data, setDataState] = useState<TData>(defaultValues as TData);
  const [hydrated, setHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [reloadCount, setReloadCount] = useState(0);

  const lastSavedSnapshotRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(false);
  const writeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const serializeData = useCallback((value: TData) => JSON.stringify(value), []);

  const applyLoadedData = useCallback(
    (loadedValue: unknown): TData => {
      const parsedValue = isPersistedEnvelope<TData>(loadedValue) ? loadedValue.data : loadedValue;
      const validatedValue = validate
        ? validate(parsedValue)
          ? parsedValue
          : undefined
        : (parsedValue as TData | undefined);

      if (defaultValues !== undefined) {
        if (validatedValue === undefined || validatedValue === null) {
          return defaultValues;
        }
        return merge ? merge(defaultValues, validatedValue) : ({ ...defaultValues, ...validatedValue } as TData);
      }

      return (validatedValue as TData) ?? (parsedValue as TData);
    },
    [defaultValues, merge, validate],
  );

  const setData = useCallback((updater: Updater<TData>) => {
    setDataState(prev => {
      if (typeof updater === 'function') {
        return (updater as (current: TData) => TData)(prev);
      }
      return updater;
    });
  }, []);

  const reload = useCallback(() => {
    setReloadCount(prev => prev + 1);
  }, []);

  const saveSpeculativeData = useCallback(
    async ({ flagName, variantName, payload }: SaveSpeculativeDataParams): Promise<boolean> => {
      return new Promise((resolve, reject) => {
        setIsSaving(true);
        commit({
          variables: {
            input: {
              input: {
                flagName,
                variantName,
                payload: JSON.stringify(payload),
              },
            },
          },
          onCompleted: (response: ExperimentsRecordExperimentsSpeculativeDataMutation['response']) => {
            if (response.upsertExperimentsSpeculativeData?.errors?.length) {
              const errorMessages = response.upsertExperimentsSpeculativeData.errors
                .map((err: { readonly __typename: string; readonly message?: string }) => {
                  if (err.__typename === '%other') {
                    return null;
                  }
                  return err.message;
                })
                .filter((msg: string | null | undefined): msg is string => Boolean(msg))
                .join(', ');
              console.error('Failed to save speculative data:', errorMessages);
              setIsSaving(false);
              reject(new Error(errorMessages));
            } else {
              setIsSaving(false);
              resolve(true);
            }
          },
          onError: error => {
            console.error('Network error saving speculative data:', error);
            setIsSaving(false);
            reject(error);
          },
        });
      });
    },
    [commit],
  );

  useEffect(() => {
    if (!enabled || !publicUserId || !flagName || !variantName) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchQuery<ExperimentsGetSpeculativeDataQuery>(
      environment,
      getExperimentSpeculativeData,
      { userId: publicUserId },
      { fetchPolicy: 'network-only' },
    )
      .toPromise()
      .then(response => {
        if (cancelled) {
          return;
        }

        const records = response?.userByPublicUserId?.experimentsSpeculativeData ?? [];
        const matchingRecord = records.find(
          record => record?.flagName === flagName && record?.variantName === variantName,
        );
        const loadedPayload = parsePayloadRootElement(matchingRecord?.payload?.rootElement);
        const nextData = applyLoadedData(loadedPayload);
        setDataState(nextData);

        const snapshot = serializeData(nextData);
        lastSavedSnapshotRef.current = snapshot;
        hasHydratedRef.current = true;
        setHydrated(true);
        setIsLoading(false);
      })
      .catch(loadError => {
        if (cancelled) {
          return;
        }

        const normalizedError = loadError instanceof Error ? loadError : new Error('Failed to load speculative data');
        setError(normalizedError);

        const fallbackData = applyLoadedData(null);
        setDataState(fallbackData);
        lastSavedSnapshotRef.current = serializeData(fallbackData);
        hasHydratedRef.current = true;
        setHydrated(true);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyLoadedData, enabled, environment, flagName, publicUserId, reloadCount, serializeData, variantName]);

  useEffect(() => {
    if (!enabled || !userNodeId || !flagName || !variantName || !hasHydratedRef.current) {
      return;
    }

    const currentSnapshot = serializeData(data);
    if (currentSnapshot === lastSavedSnapshotRef.current) {
      return;
    }

    if (writeTimeoutRef.current) {
      clearTimeout(writeTimeoutRef.current);
    }

    writeTimeoutRef.current = setTimeout(() => {
      const payload: PersistedPayloadEnvelope<TData> = {
        version: 1,
        savedAt: new Date().toISOString(),
        data,
      };

      void saveSpeculativeData({
        userNodeId,
        flagName,
        variantName,
        payload,
      })
        .then(() => {
          lastSavedSnapshotRef.current = currentSnapshot;
          setError(null);
        })
        .catch(saveError => {
          const normalizedError = saveError instanceof Error ? saveError : new Error('Failed to save speculative data');
          setError(normalizedError);
        });
    }, debounceMs);

    return () => {
      if (writeTimeoutRef.current) {
        clearTimeout(writeTimeoutRef.current);
      }
    };
  }, [data, debounceMs, enabled, flagName, saveSpeculativeData, serializeData, userNodeId, variantName]);

  useEffect(
    () => () => {
      if (writeTimeoutRef.current) {
        clearTimeout(writeTimeoutRef.current);
      }
    },
    [],
  );

  const hookState = useMemo(
    () => ({
      data,
      setData,
      reload,
      hydrated,
      isLoading,
      isSaving: isSaving || isInFlight,
      error,
      saveSpeculativeData,
    }),
    [data, error, hydrated, isInFlight, isLoading, isSaving, reload, saveSpeculativeData, setData],
  );

  return hookState;
}
